import { Injectable } from '@angular/core';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { CouponQueryResolverService } from '../coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '../coupon-query-resolver/coupon-use-cases';
import { CouponAggregationService } from '../coupon-aggregation/coupon-aggregation.service';
import { XGritApiService } from '../xgrit-api/xgrit-api.service';
import type { XgritPurchaseReceiptMultipriceResponse } from '../xgrit-api/xgrit-purchase-receipt-price-response';
import { ServerPricingService } from './server.pricing.service';
import { WysiwygRedactorService } from '../wysiwg-redactor/wysiwyg-redactor.service';
import { EnvService } from '../env/env.service';

/**
 * Client-side pricing service that extends ServerPricingService.
 * Inherits HTML rendering methods for SSR compatibility and adds client-specific
 * functionality like coupon aggregation and events.
 */
@Injectable({
  providedIn: 'root',
})
export class PricingService extends ServerPricingService {
  globalCDIQueryParam: string;
  protected override _couponCodeEvent = new BehaviorSubject<string | null>(null);

  constructor(
    redactor: WysiwygRedactorService,
    env: EnvService,
    private couponQueryResolverService: CouponQueryResolverService,
    private couponAggregationService: CouponAggregationService,
    private xgritApiService: XGritApiService,
  ) {
    super(redactor, env);
    this.globalCDIQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);
  }

  override get couponCodeEvent(): Observable<string> {
    return this._couponCodeEvent.asObservable();
  }

  override sendCouponEvent(coupon: string): void {
    if (!coupon || coupon.trim() === '') {
      return;
    }
    this._couponCodeEvent.next(coupon);
  }

  /**
   * COUPON OVERHAUL: Get pricing with all potential coupons sent to multiprice API
   *
   * Collects coupons from all sources (URL, cookies, Craft, builders, banners)
   * and sends them to the existing multiprice API. The API does the magic to determine
   * which coupons to apply.
   */
  getPricingWithAllCoupons(
    productIds: string[],
    craftProducts?: CraftXGritProductEntry[],
    builderCategoryCoupons?: string[],
  ): Observable<XgritPurchaseReceiptMultipriceResponse> {
    // Collect all potential coupons from various sources
    const couponCollectionRequest = this.couponAggregationService.collectAllCoupons(
      craftProducts,
      builderCategoryCoupons,
    );

    // Extract all coupon codes into a simple array
    const allCouponCodes = this.couponAggregationService.getAllCouponCodes(couponCollectionRequest);

    // Trigger coupon event for URL parameter coupons to ensure component re-rendering
    const urlParameterCoupons = Object.values(couponCollectionRequest.coupons.urlParameters).filter(Boolean);
    if (urlParameterCoupons.length > 0) {
      this.sendCouponEvent(urlParameterCoupons.join(', '));
    }

    // Create price set for existing multiprice API
    const priceSetList = [
      {
        productIdList: productIds,
        couponCodeList: allCouponCodes, // Send ALL coupons - API will choose!
        url: couponCollectionRequest.url, // Include URL for ambassador coupon tracking
      },
    ];

    // Use existing multiprice API - it will do the magic!
    return this.xgritApiService.getMultiProductPriceInfo(priceSetList).pipe(
      map((response) => {
        // Broadcast that we sent coupons (for any listening components)
        if (allCouponCodes.length > 0) {
          this.sendCouponEvent(allCouponCodes.join(', '));
        }

        return response;
      }),
      catchError(() => {
        return of({ priceSetList: [] }); // Fallback
      }),
    );
  }
}
